import React, { useCallback, useRef, useState } from 'react';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { ExtendedColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';

interface Props<TData, TValue> {
	column: ExtendedColumn<TData, TValue>;
	placeholder?: string;
}

export const DateRangePickerFilter = <TData, TValue>({
	column,
	placeholder,
}: Props<TData, TValue>) => {
	const { getFilterValue, setFilterValue } = column;
	const pendingValue = useRef<DatesRangeValue>([null, null]);
	const [opened, setOpened] = useState(false);

	const current = getFilterValue();
	const value: DatesRangeValue = Array.isArray(current)
		? [current[0] ?? null, current[1] ?? null]
		: [null, null];

	const handleChange = (range: DatesRangeValue) => {
		pendingValue.current = range;
		const hasValue = range[0] != null || range[1] != null;
		setFilterValue(hasValue ? (range as unknown) : null);

		if (range[0] != null && range[1] != null) {
			setOpened(false);
		}
	};

	const handlePopoverClose = useCallback(() => {
		const [from, to] = pendingValue.current;
		if (from != null && to == null) {
			setFilterValue([from, from] as unknown);
		}
		setOpened(false);
	}, [setFilterValue]);

	return (
		<DatePickerInput
			type="range"
			value={value}
			onChange={handleChange}
			valueFormat="DD/MM/YYYY"
			labelSeparator="-"
			placeholder={placeholder}
			clearable
			size="sm"
			allowSingleDateInRange
			onClick={() => setOpened(true)}
			popoverProps={{
				opened,
				onClose: handlePopoverClose,
				withinPortal: true,
				zIndex: Z_INDEX.MENU,
			}}
			styles={{
				input: {
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					height: 'var(--input-height)',
					minHeight: 'var(--input-height)',
					paddingBlock: 0,
					display: 'flex',
					alignItems: 'center',
					borderColor: defaultTheme.v3.colors.gray[4],
					'&:hover': {
						borderColor: defaultTheme.v3.colors.dark[0],
					},
					'&:focus': {
						borderColor: defaultTheme.v3.colors.dark[0],
					},
					'&:focus-within': {
						borderColor: defaultTheme.v3.colors.dark[0],
					},
				},
				placeholder: { color: defaultTheme.v3.colors.gray[9] },
			}}
		/>
	);
};
